import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';
import { useWordContext, SUPPORTED_LANGUAGES } from './WordContext';

// Create Context
const WordCountContext = createContext();

// Helper function to get userId from JWT token
const getUserId = () => {
    // Always try to get the latest userId from the JWT token
    const token = localStorage.getItem('access_token');
    if (token) {
        try {
            // Decode JWT token to get user info
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            
            const decodedToken = JSON.parse(jsonPayload);
            if (decodedToken.sub) {
                return decodedToken.sub;
            }
        } catch (err) {
            logger.error("WordCountContext - Error decoding token:", err);
        }
    }
    
    logger.warn("WordCountContext - No userId available from token");
    return null;
};

export const WordCountProvider = ({ children }) => {
    const [wordCounts, setWordCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [sentenceCounts, setSentenceCounts] = useState({});
    const [lastRefresh, setLastRefresh] = useState(null);
    const { savedWords, savedKanji, refreshSavedWords, fetchStatus } = useWordContext();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastSentenceCountsFetch, setLastSentenceCountsFetch] = useState(null);

    // Calculate word counts from savedWords context - memoized to avoid dependency cycles
    const calculateWordCounts = useCallback(() => {
        // Only update if savedWords has content
        if (Object.keys(savedWords).length === 0) {
            logger.debug('WordCountContext - No savedWords data available for count calculation');
            return null;
        }

        const counts = {};
        
        // Count words for each language
        Object.keys(savedWords).forEach(language => {
            if (savedWords[language]) {
                // Check if it's an array and get the length, or use size if it's a Set, or default to 0
                counts[language] = Array.isArray(savedWords[language]) 
                    ? savedWords[language].length 
                    : (savedWords[language].size || 0);
                logger.debug(`WordCountContext - Calculated ${counts[language]} words for ${language}`);
            }
        });
        
        // Special handling for Japanese
        if (savedWords['Japanese']) {
            // Store the separate counts as properties
            counts['Japanese_words'] = Array.isArray(savedWords['Japanese']) 
                ? savedWords['Japanese'].length 
                : (savedWords['Japanese'].size || 0);
            counts['Japanese_kanji'] = savedKanji ? savedKanji.length : 0;
            
            // For display purposes in Profile page, we now keep the counts separate
            logger.debug(`WordCountContext - Japanese words: ${counts['Japanese_words']}, kanji: ${counts['Japanese_kanji']}`);
        }
        
        // Make sure all supported languages have an entry (even if 0)
        SUPPORTED_LANGUAGES.forEach(lang => {
            if (!counts[lang] && counts[lang] !== 0) {
                counts[lang] = 0;
                logger.debug(`WordCountContext - Set default count 0 for ${lang}`);
            }
        });
        
        setWordCounts(counts);
        setLoading(false);
        setLastRefresh(Date.now());
        
        logger.debug('WordCountContext - Updated word counts:', counts);
        return counts;
    }, [savedWords, savedKanji]);  // Add dependencies here

    // Define the updateLocalCounts function that was missing
    const updateLocalCounts = useCallback(() => {
        logger.debug('WordCountContext - Updating local counts from saved words and kanji');
        calculateWordCounts();
    }, [calculateWordCounts]);

    // Listen for word data refresh events
    useEffect(() => {
        // Variables for debouncing sentence count updated events
        let sentenceCountUpdateTimer = null;
        
        const handleWordDataRefreshed = (event) => {
            logger.debug('WordCountContext - Detected word data refresh event, recalculating counts');
            calculateWordCounts();
            
            // Fetch sentence counts only when a manual refresh is triggered
            // or we haven't fetched in at least 5 minutes (300000ms)
            const now = Date.now();
            if (!lastSentenceCountsFetch || now - lastSentenceCountsFetch > 300000) {
                fetchSentenceCounts();
            }
        };
        
        const handleSentenceCountUpdated = (event) => {
            logger.debug(`WordCountContext - Detected sentence count update event: ${event.detail?.action} for ${event.detail?.language}`);
            
            // Clear any existing timer to debounce rapid updates
            if (sentenceCountUpdateTimer) {
                clearTimeout(sentenceCountUpdateTimer);
            }
            
            // Set a new timer that will only execute after 500ms of inactivity
            sentenceCountUpdateTimer = setTimeout(() => {
                logger.debug('WordCountContext - Debounced sentence count update triggered, fetching counts');
                fetchSentenceCounts();
            }, 500);
        };
        
        window.addEventListener('wordDataRefreshed', handleWordDataRefreshed);
        window.addEventListener('sentenceCountUpdated', handleSentenceCountUpdated);
        
        return () => {
            window.removeEventListener('wordDataRefreshed', handleWordDataRefreshed);
            window.removeEventListener('sentenceCountUpdated', handleSentenceCountUpdated);
            
            // Clean up any pending timer on unmount
            if (sentenceCountUpdateTimer) {
                clearTimeout(sentenceCountUpdateTimer);
            }
        };
    }, [calculateWordCounts, lastSentenceCountsFetch]);
    
    // Update word counts when savedWords or savedKanji changes
    // But don't trigger API calls on every change
    useEffect(() => {
        if (Object.keys(savedWords).length > 0) {
            calculateWordCounts();
        }
    }, [savedWords, savedKanji, calculateWordCounts]);

    // Fetch word counts for all languages
    const fetchAllLanguageWordCounts = async (forceRefresh = false) => {
        try {
            setLoading(true);
            logger.debug(`WordCountContext - Fetching counts for all languages (forceRefresh: ${forceRefresh})`);
            
            // Force refresh of savedWords from the WordContext if requested
            if (forceRefresh) {
                logger.debug('WordCountContext - Force refreshing word data from WordContext');
                const result = await refreshSavedWords();
                
                if (!result.success) {
                    logger.warn('WordCountContext - Word refresh was not successful:', result.error);
                }
            } else {
                logger.debug('WordCountContext - Using existing word data (no force refresh)');
                // Calculate fresh counts from current savedWords
                calculateWordCounts();
            }
            
            // Fetch sentence counts for all languages
            await fetchSentenceCounts();
            
            setLoading(false);
            logger.debug('WordCountContext - Completed refresh of all count data');
            return wordCounts;
        } catch (error) {
            logger.error('WordCountContext - Error fetching all language word counts:', error);
            setLoading(false);
            return null;
        }
    };

    // Get separate word and kanji counts for Japanese
    const getJapaneseWordCount = () => {
        if (!savedWords.Japanese) return 0;
        return Array.isArray(savedWords.Japanese) 
            ? savedWords.Japanese.length 
            : (savedWords.Japanese.size || 0);
    };
    
    const getKanjiCount = () => {
        if (!savedKanji) return 0;
        return savedKanji.length;
    };

    // Fetch sentence review counts from API
    const fetchSentenceCounts = async () => {
        try {
            // If we've recently fetched (within the last 10 seconds), use the cached data
            const now = Date.now();
            
            // Global timestamp to track when the last API call was made
            // This helps prevent multiple components from making the same request
            if (!window.GLOBAL_LAST_SENTENCE_COUNT_FETCH) {
                window.GLOBAL_LAST_SENTENCE_COUNT_FETCH = 0;
            }
            
            // Add a more aggressive throttle - no more than one call every 2 seconds globally
            if (now - window.GLOBAL_LAST_SENTENCE_COUNT_FETCH < 2000) { // 2 seconds
                logger.debug('WordCountContext - Too many sentence count requests, throttling');
                return sentenceCounts;
            }
            
            // Check individual component cache (10 seconds)
            if (lastSentenceCountsFetch && (now - lastSentenceCountsFetch < 10000)) { // 10 seconds
                logger.debug('WordCountContext - Using cached sentence counts (fetched in the last 10s)');
                return sentenceCounts;
            }
            
            // To prevent multiple simultaneous API calls, we use a debounce approach
            // We track if a fetch is in progress with this flag
            if (window.sentenceCountsFetchInProgress) {
                logger.debug('WordCountContext - Sentence counts fetch already in progress, skipping duplicate call');
                return sentenceCounts;
            }
            
            window.sentenceCountsFetchInProgress = true;
            // Update the global timestamp
            window.GLOBAL_LAST_SENTENCE_COUNT_FETCH = now;
            
            logger.debug('WordCountContext - Fetching sentence counts from server');
            
            // We'll fetch sentence counts for all languages at once
            const response = await apiClient.get('/api/getSentenceCounts');
            
            // Clear the in-progress flag
            window.sentenceCountsFetchInProgress = false;
            
            if (response.ok) {
                const data = await response.json();
                setSentenceCounts(data.counts || {});
                setLastSentenceCountsFetch(Date.now());
                logger.debug('WordCountContext - Fetched sentence counts:', data.counts);
                return data.counts;
            } else {
                const error = `Failed to fetch sentence counts: ${response.status}`;
                logger.error('WordCountContext - ' + error);
                return null;
            }
        } catch (error) {
            // Clear the in-progress flag in case of error
            window.sentenceCountsFetchInProgress = false;
            logger.error('WordCountContext - Error fetching sentence counts:', error);
            return null;
        }
    };

    // Refresh word counts from the server - keep this for manual refresh from Profile.js
    const refreshCounts = useCallback(async () => {
        const userId = getUserId();
        if (!userId) {
            logger.debug('WordCountContext - No user ID available, skipping word count refresh');
            return;
        }
        
        try {
            // Check if user is authenticated before making API calls
            if (!localStorage.getItem('access_token')) {
                logger.debug('WordCountContext - No access token available, skipping word count refresh');
                return;
            }

            setIsRefreshing(true);
            logger.debug('WordCountContext - Refreshing word counts (manual refresh triggered)');
            
            // Calculate local counts
            calculateWordCounts();
            
            // Always fetch sentence counts on manual refresh
            await fetchSentenceCounts();
            
            // Success - we've updated the counts locally
            logger.debug('WordCountContext - Word counts and sentence counts refreshed');
            
        } catch (error) {
            logger.error('WordCountContext - Error refreshing counts:', error);
        } finally {
            setIsRefreshing(false);
        }
    }, [calculateWordCounts]);

    // Fetch word counts on first load
    useEffect(() => {
        const fetchInitialCounts = async () => {
            const userId = getUserId();
            if (!userId) {
                logger.warn('WordCountContext - No user ID available for initial fetch');
                return;
            }
            
            setLoading(true);
            try {
                // Use the refreshCounts which handles kanji properly
                await refreshCounts();
                logger.debug('WordCountContext - Initial counts loaded');
            } catch (error) {
                logger.error('WordCountContext - Error during initial counts fetch:', error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchInitialCounts();
    }, [refreshCounts]);

    // Update when saved words change with successful fetch
    // This handles updates after words are saved/deleted
    useEffect(() => {
        if (fetchStatus === 'success') {
            logger.debug('WordCountContext - Words fetched successfully, updating counts');
            calculateWordCounts();
            
            // Only fetch sentence counts occasionally, not on every word update
            const now = Date.now();
            if (!lastSentenceCountsFetch || now - lastSentenceCountsFetch > 300000) { // 5 minutes
                fetchSentenceCounts().catch(error => {
                    logger.error('WordCountContext - Error fetching sentence counts on word update:', error);
                });
            }
            
            setLastRefresh(new Date());
        }
    }, [fetchStatus, calculateWordCounts, lastSentenceCountsFetch]);

    return (
        <WordCountContext.Provider
            value={{
                wordCounts,
                sentenceCounts,
                loading,
                lastRefresh,
                refreshCounts,
                supportedLanguages: SUPPORTED_LANGUAGES,
                getJapaneseWordCount,
                getKanjiCount
            }}
        >
            {children}
        </WordCountContext.Provider>
    );
};

// Custom hook to use the WordCountContext
export const useWordCount = () => {
    const context = useContext(WordCountContext);
    
    if (!context) {
        throw new Error('useWordCount must be used within a WordCountProvider');
    }
    
    return context;
};

export default WordCountContext; 